import { capitalize } from 'inflected';
import isMac from './isMac';

const SHORTCUT_KEY_SEPARATOR = '﹢';
const MAC_SHORTCUT_KEY_SEPARATOR = ' ';

const nonMacMap = {
  shift: { replacement: 'Shift', place: 0 },
  cmd: { replacement: 'Ctrl', place: 2 },
  command: { replacement: 'Ctrl', place: 2 },
  option: { replacement: 'Alt', place: 4 },
};

const macMap = {
  ctrl: { replacement: '⋀', place: 0 },
  option: { replacement: '⌥', place: 2 },
  shift: { replacement: '⇧', place: 4 },
  cmd: { replacement: '⌘', place: 10 },
  command: { replacement: '⌘', place: 10 },
  del: { replacement: '⌫', place: 100 },
  backspace: { replacement: '⌫', place: 100 },
};

/**
 * new Shortcut('Cmd+V')
 *
 * All shortcuts should be defined with mac modifier keys (cmd, option, etc...)
 * Non mac equivalents will be generated by the class.
 *
 * To access the shortcut string for binding, use `.forCurrentPlatform`;
 *
 * To access the shortcut string for display, use `.pretty`;
 */
export default class Shortcut {
  private _shortcut: string = '';
  private _shortcutForCurrentPlatform: string = '';
  private _pretty: string = '';

  constructor(rawValue: string, pretty?: string) {
    this.rawValue = rawValue;

    if (pretty) {
      this._pretty = pretty;
    }
  }

  get rawValue() {
    return this._shortcut;
  }

  set rawValue(value: string) {
    this._shortcut = value;
    this._shortcutForCurrentPlatform = Shortcut.modifyForCurrentPlatform(
      this._shortcut
    );
    this._pretty = Shortcut.prettify(this._shortcut);
  }

  /**
   * @returns string The shortcut string for the current platform.
   *
   * @example
   *```
   * (new Shortcut('Cmd+V')).forCurrentPlatform
   *```
   *     mac: 'Cmd+V,Ctrl+V'
   * not mac: 'Ctrl+V'
   *
   */
  get forCurrentPlatform() {
    return this._shortcutForCurrentPlatform;
  }

  /**
   * @returns The pretty formatted string of the shortcut
   * @example
   * ```
   * (new Shortcut('Cmd+V')).pretty
   * ```
   *     mac: '⌘ V'
   * not mac: 'Ctrl+V'
   */
  get pretty() {
    return this._pretty;
  }

  private static _splitKeys(shortcut: string): string[] {
    return shortcut
      .replace(/\s/g, '')
      .toLowerCase()
      .split(',')
      .reduce((keys, key, i, all) => {
        return key === ''
          ? keys
          : [...keys, key + (all[i + 1] === '' ? ',' : '')];
      }, []);
  }

  /**
   * @returns the shortcut for the current platform given a mac shortcut.
   * We accept both Cmd and Ctrl for any Cmd defined shortcut on mac.
   *
   * @example
   *```
   * Shortcut.modifyForCurrentPlatform('cmd+option+x')
   *```
   *   windows: 'ctrl+alt+x'
   *       mac: 'cmd+option+x,ctrl+option+x'
   */
  static modifyForCurrentPlatform(shortcut: string): string {
    if (!shortcut || shortcut.length === 0) return '';

    const split = Shortcut._splitKeys(shortcut);

    return split
      .map((base) => {
        const modified = base
          .split('+')
          .map((s) => nonMacMap[s]?.replacement || s)
          .join('+');

        return isMac() && modified.toLowerCase() !== base.toLowerCase()
          ? `${base},${modified}`
          : modified;
      })
      .join(',');
  }

  /**
   * @returns the pretty string for a hotkeys.js shortcut
   *
   * @example
   *```
   * Shortcut.prettifyShortcut('command+shift+x')
   *```
   *   windows: 'Ctrl﹢Shift﹢X'
   *       mac: '⇧ ⌘ X'
   */
  static prettify(shortcut: string): string {
    if (!shortcut || shortcut.length === 0) return '';

    // We only display the first available shortcut.
    shortcut = Shortcut._splitKeys(shortcut)[0];

    const map = isMac() ? macMap : nonMacMap;

    const pieces = shortcut.split('+').map((part, i) => {
      const mapped = map[part.trim().toLowerCase()];
      return mapped || { replacement: part, place: 10000 + i };
    });

    return pieces
      .sort((a, b) => a.place - b.place)
      .map(({ replacement }) => capitalize(replacement.toLowerCase()))
      .join(isMac() ? MAC_SHORTCUT_KEY_SEPARATOR : SHORTCUT_KEY_SEPARATOR);
  }
}
